/* eslint-disable react/jsx-handler-names */
import React, {
  useState,
  useCallback,
  useMemo,
  useContext,
  createContext,
} from 'react';

import Toast from '../components/Toast';
import DialogModal from '../modals/DialogModal';

const UiContext = createContext({
  showToast: () => {},
  showDialogModal: () => {},
  showApiErrorToast: () => {},
  showSuccessToast: () => {},
});

export default function UiContextProvider({ children }) {
  const [toastData, setToastData] = useState({
    type: '',
    text: '',
  });
  const [isToastVisible, setIsToastVisible] = useState(false);
  const [dialogModalData, setDialogModalData] = useState({
    title: '',
    text: '',
    onConfirm: () => {},
    type: '',
    confirmButtonLabel: '',
    cancelButtonLabel: '',
  });
  const [isDialogModalVisible, setIsDialogModalVisible] = useState(false);

  const showToast = useCallback(({ type, text }) => {
    setToastData({ type, text });
    setIsToastVisible(true);
  }, []);

  const showApiErrorToast = useCallback(
    (error) => {
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
    [showToast]
  );

  const showSuccessToast = useCallback(
    (text) => {
      showToast({ text, type: 'success' });
    },
    [showToast]
  );

  const showDialogModal = useCallback(
    ({
      title,
      message,
      onConfirm,
      type,
      confirmButtonLabel,
      cancelButtonLabel,
    }) => {
      setDialogModalData({
        title,
        message,
        onConfirm,
        type,
        confirmButtonLabel,
        cancelButtonLabel,
      });
      setIsDialogModalVisible(true);
    },
    []
  );

  const value = useMemo(
    () => ({ showToast, showDialogModal, showApiErrorToast, showSuccessToast }),
    [showToast, showDialogModal, showApiErrorToast, showSuccessToast]
  );

  return (
    <UiContext.Provider value={value}>
      {children}
      <Toast
        open={isToastVisible}
        setOpen={setIsToastVisible}
        type={toastData.type}
        text={toastData.text}
      />
      <DialogModal
        isOpen={isDialogModalVisible}
        setIsOpen={setIsDialogModalVisible}
        title={dialogModalData.title}
        message={dialogModalData.message}
        onConfirm={dialogModalData.onConfirm}
        type={dialogModalData.type}
        confirmButtonLabel={dialogModalData.confirmButtonLabel}
        cancelButtonLabel={dialogModalData.cancelButtonLabel}
      />
    </UiContext.Provider>
  );
}

export const useUiContext = () => useContext(UiContext);
